/**
 * MissionControlPage — shell for the /mission-control/* surface.
 *
 * Responsibilities:
 *   1. Auth-guard: requires admin token, bounces to /admin/login if absent.
 *   2. Cockpit / paper mode toggle (localStorage key "mc_cockpit_mode").
 *   3. Listens for CustomEvent "mc:toggleCockpit", "mc:openSettings",
 *      "mc:openSearch".
 *   4. Renders PulseBar (sticky 44px), Sidebar (fixed 72px), room content,
 *      and a slim footer status bar (28px).
 *   5. Polls /api/admin/ops/decisions every 15 s for the Sidebar badge.
 *   6. Phase F: SearchPanel (⌘K) + ShortcutsOverlay (?) + useShortcuts.
 *
 * Cockpit mode: dark surface, tight information density.
 * Paper mode:   light #FAF8F4 surface matching the main brand palette.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";

import PulseBar          from "@/components/mission-control/PulseBar";
import Sidebar           from "@/components/mission-control/Sidebar";
import SearchPanel       from "@/components/mission-control/SearchPanel";
import ShortcutsOverlay  from "@/components/mission-control/ShortcutsOverlay";
import SettingsPanel     from "@/components/mission-control/SettingsPanel";
import { useShortcuts }  from "@/components/mission-control/hooks/useShortcuts";

import NowRoom       from "@/components/mission-control/rooms/NowRoom";
import DecisionsRoom from "@/components/mission-control/rooms/DecisionsRoom";
import AgentsRoom    from "@/components/mission-control/rooms/AgentsRoom";
import PeopleRoom    from "@/components/mission-control/rooms/PeopleRoom";
import MoneyRoom     from "@/components/mission-control/rooms/MoneyRoom";
import CompassRoom   from "@/components/mission-control/rooms/CompassRoom";
import LegalRoom     from "@/components/mission-control/rooms/LegalRoom";
import "@/components/mission-control/mc.css";

// ── GST footer clock ──────────────────────────────────────────────────────────

function FooterClock() {
  const [time, setTime] = useState("");

  useEffect(() => {
    function tick() {
      setTime(
        new Date().toLocaleTimeString("en-US", {
          timeZone: "Asia/Dubai",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      );
    }
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <span className="mc-mono mc-footer-time" title="Gulf Standard Time (GST)">
      {time} GST
    </span>
  );
}

// ── auth headers helper ───────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── MissionControlPage ────────────────────────────────────────────────────────

export default function MissionControlPage() {
  const navigate = useNavigate();

  // ── 1. Auth guard ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  // ── 2. Cockpit / paper mode ──────────────────────────────────────────────────
  const [cockpitMode, setCockpitMode] = useState(() => {
    try {
      const stored = localStorage.getItem("mc_cockpit_mode");
      // Default: cockpit on (true)
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });

  const toggleCockpit = useCallback(() => {
    setCockpitMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("mc_cockpit_mode", String(next));
      } catch { /* storage may be blocked */ }
      return next;
    });
  }, []);

  // ── 3. Phase F: Search + Shortcuts overlay ───────────────────────────────────
  const [showSearch,    setShowSearch]    = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);

  const openSearch    = useCallback(() => setShowSearch(true),    []);
  const openShortcuts = useCallback(() => setShowShortcuts(true), []);
  const openSettings  = useCallback(() => setShowSettings(true),  []);

  // Global keyboard shortcuts: g+n/d/a/p/m/c/l, ⌘K, ?, ⌘.
  useShortcuts({
    onIAmDriving:       null,  // PulseBar handles its own override toggle
    onSearch:           openSearch,
    onShortcutsOverlay: openShortcuts,
  });

  // ── 4. CustomEvent listeners ─────────────────────────────────────────────────
  useEffect(() => {
    function onToggleCockpit() { toggleCockpit(); }
    function onOpenSettings()  { openSettings(); }
    function onOpenSearch()    { openSearch(); }

    window.addEventListener("mc:toggleCockpit", onToggleCockpit);
    window.addEventListener("mc:openSettings",  onOpenSettings);
    window.addEventListener("mc:openSearch",    onOpenSearch);
    return () => {
      window.removeEventListener("mc:toggleCockpit", onToggleCockpit);
      window.removeEventListener("mc:openSettings",  onOpenSettings);
      window.removeEventListener("mc:openSearch",    onOpenSearch);
    };
  }, [toggleCockpit, openSearch, openSettings]);

  // ── 5. Pending decisions poll (15 s) for Sidebar badge ──────────────────────
  const [pendingDecisions, setPendingDecisions] = useState(0);
  const [isManualOverrideActive, setIsManualOverrideActive] = useState(false);

  const fetchDecisions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/admin/ops/decisions?include_decided=false&limit=50`,
        { headers: authHeaders() }
      );
      const decisions = res.data?.decisions || [];
      setPendingDecisions(decisions.filter((d) => !d.decided_at).length);
    } catch {
      // keep stale value
    }
  }, []);

  // Also poll agents to track manual override state for footer indicator
  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/agents`, {
        headers: authHeaders(),
      });
      const agents = (res.data?.agents || []).filter((a) => a.kind === "platform");
      const overrideActive = agents.some(
        (a) =>
          a.status === "paused" &&
          typeof a.paused_reason === "string" &&
          a.paused_reason.startsWith("manual_override:")
      );
      setIsManualOverrideActive(overrideActive);
    } catch {
      // keep stale value
    }
  }, []);

  useEffect(() => {
    fetchDecisions();
    fetchAgents();
    const t1 = setInterval(fetchDecisions, 15_000);
    const t2 = setInterval(fetchAgents, 30_000);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [fetchDecisions, fetchAgents]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div
      className={`mc-shell${cockpitMode ? " mc-shell--cockpit" : " mc-shell--paper"}`}
      data-cockpit={cockpitMode ? "true" : "false"}
    >
      {/* ── Top bar ── */}
      <PulseBar />

      {/* ── Body: sidebar + room content ── */}
      <div className="mc-body">
        <Sidebar
          pendingDecisions={pendingDecisions}
          openLegalTickets={0}
        />

        <main className="mc-main" id="mc-main-content">
          <Routes>
            {/* Default → Now room */}
            <Route index element={<Navigate to="now" replace />} />

            {/* Phase A */}
            <Route path="now"       element={<NowRoom />}       />

            {/* Phase B */}
            <Route path="decisions" element={<DecisionsRoom />} />

            {/* Phase C */}
            <Route path="agents"    element={<AgentsRoom />}    />
            <Route path="legal"     element={<LegalRoom />}     />

            {/* Phase D */}
            <Route path="people"    element={<PeopleRoom />}    />
            <Route path="money"     element={<MoneyRoom />}     />

            {/* Phase E */}
            <Route path="compass"   element={<CompassRoom />}   />
          </Routes>
        </main>
      </div>

      {/* ── Phase F: Search panel + Shortcuts overlay + Settings ── */}
      <SearchPanel
        open={showSearch}
        onClose={() => setShowSearch(false)}
      />
      <ShortcutsOverlay
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
      <SettingsPanel
        open={showSettings}
        onClose={() => setShowSettings(false)}
        cockpitMode={cockpitMode}
        onToggleCockpit={toggleCockpit}
      />

      {/* ── Footer ── */}
      <footer className="mc-footer" aria-label="Mission Control status bar">

        {/* Brand lockup */}
        <div className="mc-footer-brand">
          <span className="mc-logo-mark mc-logo-mark--sm" aria-hidden="true">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <path d="M5 12.5 L10 17.5 L19 7" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className="mc-footer-brand-name">Xpert</span>
          <span className="mc-footer-brand-sep" aria-hidden="true">·</span>
          <span className="mc-footer-brand-sub">Mission Control</span>
        </div>

        <span className="mc-footer-spacer" />

        {/* Copyright */}
        <span className="mc-footer-copy">
          © {new Date().getFullYear()} Xpert
        </span>

        <div className="mc-footer-divider" aria-hidden="true" />

        {isManualOverrideActive && (
          <span className="mc-footer-override" role="status" aria-live="polite">
            ⚠ I AM DRIVING
          </span>
        )}

        <FooterClock />
      </footer>
    </div>
  );
}
